<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { subDays, fromUnixTime } from 'date-fns';
import { reportPeriodUnix } from 'dashboard/helper/reportPeriod';
import { useReportTimezone } from 'dashboard/composables/useReportTimezone';
import {
  buildFilterList,
  buildRatingsList,
  getActiveFilter,
  getFilterType,
} from './CsatFilterHelpers';
import FilterButton from 'dashboard/components/ui/Dropdown/DropdownButton.vue';
import ActiveFilterChip from '../Filters/v3/ActiveFilterChip.vue';
import AddFilterChip from '../Filters/v3/AddFilterChip.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import ReportFilterPanel from '../ReportFilterPanel.vue';
import {
  parseReportURLParams,
  parseFilterURLParams,
  generateCompleteURLParams,
} from '../../helpers/reportFilterHelper';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';
import { useShellPeriod } from '../../composables/useShellPeriod';
import { rangeLabelKey } from '../../helpers/reportsPeriod';

const props = defineProps({
  beforeChange: { type: Function, default: () => true },
  showTeamFilter: {
    type: Boolean,
    default: false,
  },
  showRatingFilter: {
    type: Boolean,
    default: true,
  },
  showInboxFilter: {
    type: Boolean,
    default: true,
  },
  errorMessage: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['filterChange', 'retry']);

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();
const { isWorkspaceV2 } = useWorkspaceV2();

// Initialize from URL params immediately
const urlParams = parseReportURLParams(route.query);
const urlFilters = parseFilterURLParams(route.query);

const initialDateRange =
  urlParams.from && urlParams.to
    ? [fromUnixTime(urlParams.from), fromUnixTime(urlParams.to)]
    : [subDays(new Date(), 6), new Date()];

const datePickerVersion = ref(0);
const showDropdownMenu = ref(false);
const showSubDropdownMenu = ref(false);
const activeFilterType = ref('');
const customDateRange = ref(initialDateRange);
const selectedDateRange = ref(urlParams.range || DATE_RANGE_TYPES.LAST_7_DAYS);

const appliedFilters = ref({
  user_ids: urlFilters.agent_id,
  inbox_id: urlFilters.inbox_id,
  team_id: urlFilters.team_id,
  rating: urlFilters.rating,
});

const agents = computed(() => store.getters['agents/getAgents']);
const inboxes = computed(() => store.getters['inboxes/getInboxes']);
const teams = computed(() => store.getters['teams/getTeams']);

const ratings = computed(() => buildRatingsList(t));

// Bordas do periodo no fuso da CONTA, nao no do navegador.
const { reportTimezone } = useReportTimezone();
const reportPeriod = computed(() =>
  reportPeriodUnix({
    dates: customDateRange.value,
    rangeType: selectedDateRange.value,
    timeZone: reportTimezone.value,
  })
);
const from = computed(() => reportPeriod.value.from);
const to = computed(() => reportPeriod.value.to);

const getFilterSource = type => {
  const sources = {
    agents: agents.value,
    inboxes: inboxes.value,
    teams: teams.value,
    ratings: ratings.value,
  };
  return sources[type] || [];
};

const getFilterOptions = type => {
  if (type === 'ratings') {
    return ratings.value;
  }
  return buildFilterList(getFilterSource(type), type);
};

const filterListMenuItems = computed(() => {
  const filterTypes = [
    {
      id: '1',
      name: t('CSAT_REPORTS.FILTERS.AGENTS.LABEL'),
      type: 'agents',
    },
  ];

  if (props.showInboxFilter) {
    filterTypes.push({
      id: '2',
      name: t('CSAT_REPORTS.FILTERS.INBOXES.LABEL'),
      type: 'inboxes',
    });
  }

  if (props.showTeamFilter) {
    filterTypes.push({
      id: '4',
      name: t('CSAT_REPORTS.FILTERS.TEAMS.LABEL'),
      type: 'teams',
    });
  }

  if (props.showRatingFilter) {
    filterTypes.push({
      id: '3',
      name: t('CSAT_REPORTS.FILTERS.RATINGS.LABEL'),
      type: 'ratings',
    });
  }

  const activeFilterKeys = Object.keys(appliedFilters.value).filter(
    key => appliedFilters.value[key]
  );
  const activeFilterTypes = activeFilterKeys.map(key =>
    getFilterType(key, 'keyToType')
  );

  return filterTypes
    .filter(({ type }) => !activeFilterTypes.includes(type))
    .map(({ id, name, type }) => ({
      id,
      name,
      type,
      options: getFilterOptions(type),
    }));
});

const enabledFilterTypes = computed(() => {
  const types = new Set(['agents']);
  if (props.showInboxFilter) types.add('inboxes');
  if (props.showTeamFilter) types.add('teams');
  if (props.showRatingFilter) types.add('ratings');
  return types;
});

const activeFilters = computed(() => {
  const activeKeys = Object.keys(appliedFilters.value).filter(
    key => appliedFilters.value[key]
  );

  return activeKeys
    .map(key => {
      const filterType = getFilterType(key, 'keyToType');
      const items = getFilterSource(filterType);
      const item = getActiveFilter(
        items,
        filterType,
        appliedFilters.value[key]
      );
      const displayName =
        item?.name || item?.title || `ID: ${appliedFilters.value[key]}`;

      return {
        id: item?.id || appliedFilters.value[key],
        name: displayName,
        type: filterType,
        options: getFilterOptions(filterType),
      };
    })
    .filter(f => enabledFilterTypes.value.has(f.type));
});

const hasActiveFilters = computed(() => {
  const keys = ['user_ids'];
  if (props.showInboxFilter) keys.push('inbox_id');
  if (props.showTeamFilter) keys.push('team_id');
  if (props.showRatingFilter) keys.push('rating');
  return keys.some(key => appliedFilters.value[key]);
});

const selectedRangeLabel = computed(() =>
  t(rangeLabelKey(selectedDateRange.value))
);

const filterContext = computed(() => {
  const count = activeFilters.value.length;
  return count
    ? t('REPORT.FILTER_PANEL.CONTEXT_WITH_FILTERS', {
        range: selectedRangeLabel.value,
        count,
      })
    : selectedRangeLabel.value;
});

const canReset = computed(
  () =>
    hasActiveFilters.value ||
    selectedDateRange.value !== DATE_RANGE_TYPES.LAST_7_DAYS
);

const isAllFilterSelected = computed(() => !filterListMenuItems.value.length);

const updateURLParams = () => {
  const params = generateCompleteURLParams({
    from: from.value,
    to: to.value,
    range: selectedDateRange.value,
    filters: {
      agent_id: appliedFilters.value.user_ids,
      inbox_id: appliedFilters.value.inbox_id,
      team_id: appliedFilters.value.team_id,
      rating: appliedFilters.value.rating,
    },
  });
  router.replace({ query: params });
};

const emitChange = () => {
  updateURLParams();
  emit('filterChange', {
    from: from.value,
    to: to.value,
    selectedAgents: appliedFilters.value.user_ids
      ? [{ id: appliedFilters.value.user_ids }]
      : [],
    selectedInbox: appliedFilters.value.inbox_id
      ? { id: appliedFilters.value.inbox_id }
      : null,
    selectedTeam: appliedFilters.value.team_id
      ? { id: appliedFilters.value.team_id }
      : null,
    selectedRating: appliedFilters.value.rating
      ? { value: appliedFilters.value.rating }
      : null,
  });
};

const closeDropdown = () => {
  showDropdownMenu.value = false;
};

const closeActiveFilterDropdown = () => {
  activeFilterType.value = '';
  showSubDropdownMenu.value = false;
};

const resetDropdown = () => {
  closeDropdown();
  closeActiveFilterDropdown();
};

const addFilter = async item => {
  if (!(await props.beforeChange())) return;
  const { type, id } = item;
  const filterKey = getFilterType(type, 'typeToKey');
  appliedFilters.value[filterKey] = id;
  emitChange();
  resetDropdown();
};

const removeFilter = async type => {
  if (!(await props.beforeChange())) return;
  const filterKey = getFilterType(type, 'typeToKey');
  appliedFilters.value[filterKey] = null;
  emitChange();
};

const clearAllFilters = async () => {
  if (!(await props.beforeChange())) return;
  appliedFilters.value = {
    user_ids: null,
    inbox_id: null,
    team_id: null,
    rating: null,
  };
  emitChange();
  resetDropdown();
};

const resetFilters = async () => {
  if (!(await props.beforeChange())) return;
  customDateRange.value = [subDays(new Date(), 6), new Date()];
  selectedDateRange.value = DATE_RANGE_TYPES.LAST_7_DAYS;
  appliedFilters.value = {
    user_ids: null,
    inbox_id: null,
    team_id: null,
    rating: null,
  };
  emitChange();
  resetDropdown();
};

const showDropdown = () => {
  showSubDropdownMenu.value = false;
  showDropdownMenu.value = !showDropdownMenu.value;
};

const openActiveFilterDropdown = filterType => {
  closeDropdown();
  activeFilterType.value = filterType;
  showSubDropdownMenu.value = !showSubDropdownMenu.value;
};

const onDateRangeChange = async value => {
  if (!(await props.beforeChange())) {
    datePickerVersion.value += 1;
    return;
  }
  const [startDate, endDate, rangeType] = value;
  customDateRange.value = [startDate, endDate];
  selectedDateRange.value = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
  emitChange();
};

// Dentro da casa dos relatorios o periodo vem do seletor unico da casca (CSAT e
// Funil): o calendario desta tela some e ela segue a troca feita la.
const shellPeriod = useShellPeriod(next => {
  if (
    next.range === selectedDateRange.value &&
    next.from === from.value &&
    next.to === to.value
  )
    return;
  onDateRangeChange([next.startDay, next.endDay, next.range]);
});

onMounted(() => {
  if (!props.showInboxFilter) {
    appliedFilters.value.inbox_id = null;
  }
  if (!props.showTeamFilter) {
    appliedFilters.value.team_id = null;
  }
  if (!props.showRatingFilter) {
    appliedFilters.value.rating = null;
  }
  emitChange();
});
</script>

<template>
  <ReportFilterPanel
    :context="filterContext"
    :can-reset="canReset"
    :error-message="props.errorMessage"
    @reset="resetFilters"
    @retry="emit('retry')"
  >
    <div
      class="flex flex-col flex-wrap w-full gap-3 md:flex-row"
      :class="{ 'md:items-end': isWorkspaceV2 }"
    >
      <label
        v-if="!shellPeriod"
        :class="
          isWorkspaceV2
            ? 'flex flex-col gap-1.5 text-xs font-medium text-n-slate-11'
            : 'contents'
        "
      >
        <span v-if="isWorkspaceV2">{{ $t('REPORT.FILTER_PANEL.PERIOD') }}</span>
        <WootDatePicker
          :key="datePickerVersion"
          :date-range="customDateRange"
          :range-type="selectedDateRange"
          @date-range-changed="onDateRangeChange"
        />
      </label>

      <div
        class="flex flex-col flex-wrap items-start gap-2 md:items-center md:flex-nowrap md:flex-row"
      >
        <div
          v-if="hasActiveFilters"
          class="flex flex-wrap gap-2 md:flex-nowrap"
        >
          <ActiveFilterChip
            v-for="filter in activeFilters"
            v-bind="filter"
            :key="filter.type"
            :placeholder="
              $t(
                `CSAT_REPORTS.FILTERS.INPUT_PLACEHOLDER.${filter.type.toUpperCase()}`
              )
            "
            :active-filter-type="activeFilterType"
            :show-menu="showSubDropdownMenu"
            enable-search
            @toggle-dropdown="openActiveFilterDropdown"
            @close-dropdown="closeActiveFilterDropdown"
            @add-filter="addFilter"
            @remove-filter="removeFilter"
          />
        </div>

        <div
          v-if="hasActiveFilters && !isAllFilterSelected"
          class="w-full h-px border md:w-px md:h-5 border-n-weak"
        />

        <div class="flex items-center gap-2">
          <AddFilterChip
            v-if="!isAllFilterSelected"
            placeholder-i18n-key="CSAT_REPORTS.FILTERS.INPUT_PLACEHOLDER"
            :name="$t('CSAT_REPORTS.FILTERS.ADD_FILTER')"
            :menu-option="filterListMenuItems"
            :show-menu="showDropdownMenu"
            :empty-state-message="$t('CSAT_REPORTS.FILTERS.NO_FILTER')"
            @toggle-dropdown="showDropdown"
            @close-dropdown="closeDropdown"
            @add-filter="addFilter"
          />

          <div v-if="hasActiveFilters" class="w-px h-5 border border-n-weak" />

          <FilterButton
            v-if="hasActiveFilters"
            :button-text="$t('CSAT_REPORTS.FILTERS.CLEAR_ALL')"
            @click="clearAllFilters"
          />
        </div>
      </div>
    </div>
  </ReportFilterPanel>
</template>
